import React from 'react';
import './About.css';
import Header from '../Shared/Header/Header';
import Footer from '../Shared/Footer/Footer';
import SubHeader from '../Shared/SubHeader';
import { FaAward, FaUsers, FaHeart, FaStethoscope, FaCheckCircle, FaArrowRight, FaCertificate } from 'react-icons/fa';
import { useGetDoctorsQuery } from '../../redux/api/doctorApi';
import { Link } from 'react-router-dom';
import { Empty, Spin } from 'antd';

const About = () => {
	const { data: doctorData, isLoading: doctorIsLoading, isError: doctorIsError } = useGetDoctorsQuery({ limit: 4 });
	const doctors = doctorData?.doctors;

	const stats = [
		{ icon: <FaUsers />, number: '500+', label: 'Appointments Booked' },
		{ icon: <FaStethoscope />, number: '26', label: 'Verified Specialists' },
		{ icon: <FaAward />, number: '26', label: 'Active Specialties' },
		{ icon: <FaHeart />, number: '100%', label: 'Coordination Handled' }
	];

	const values = [
		{
			title: 'Trust',
			description: 'Every doctor on our platform is verified. Every patient interaction is handled with professionalism and care.',
			icon: <FaStethoscope />
		},
		{
			title: 'Patient First',
			description: 'Your time is valuable. We make the booking process as fast and simple as possible so you can focus on getting better.',
			icon: <FaHeart />
		},
		{
			title: 'Transparency',
			description: 'No hidden fees. No surprises. We send your invoice before you commit, so you always know exactly what to expect.',
			icon: <FaAward />
		}
	];

	let doctorContent = null;
	if (doctorIsLoading) {
		doctorContent = (
			<div className="text-center py-5">
				<Spin size="large" />
			</div>
		);
	} else if (doctorIsError) {
		doctorContent = (
			<div className="col-12 text-center py-5">
				<p>Unable to load doctors</p>
			</div>
		);
	} else if (!doctors || doctors.length === 0) {
		doctorContent = (
			<div className="col-12 py-5">
				<Empty description="No doctors available" />
			</div>
		);
	} else {
		doctorContent = doctors.map((item) => {
			const fullName = `${item?.firstName || ''} ${item?.lastName || ''}`.trim() || 'Doctor';
			const spec = item?.specialization || 'General practice';
			return (
				<div className="col-sm-6 col-lg-3 mb-4" key={item.id}>
					<article className="about-doctor-card">
						<Link to={`/doctors/profile/${item.id}`} className="about-doctor-card__media">
							<div className="about-doctor-card__photo">
								{item.img ? (
									<img src={item.img} alt={fullName} className="img-fluid" />
								) : (
									<div className="about-doctor-card__placeholder">
										<FaStethoscope />
									</div>
								)}
							</div>
							<span className="about-doctor-card__shine" aria-hidden="true" />
							<span className="about-doctor-card__chip">
								<FaCertificate /> Board-certified
							</span>
						</Link>
						<div className="about-doctor-card__body">
							<h5 className="about-doctor-card__name">{fullName}</h5>
							<p className="about-doctor-card__designation">{item?.designation || 'Physician'}</p>
							<p className="about-doctor-card__specialization">{spec}</p>
							<div className="about-doctor-card__meta">
								<span>{item?.experience ? `${item.experience} yrs exp.` : 'Experienced care'}</span>
								<span className="about-doctor-card__dot" aria-hidden="true" />
								<span>Telehealth</span>
							</div>
							<Link to={`/doctors/profile/${item.id}`} className="about-doctor-card__cta">
								View profile <FaArrowRight />
							</Link>
						</div>
					</article>
				</div>
			);
		});
	}

	return (
		<>
			<Header />
			<SubHeader title="About us" subtitle="Learn more about our mission and values" />
			
			{/* Mission Section */}
			<section className="about-mission">
				<div className="container">
					<div className="row align-items-center g-5">
						<div className="col-lg-6">
							<span className="about-label">WHO WE ARE</span>
							<h2 className="about-heading">Committed to connecting patients with the right care</h2>
							<p className="about-text">
								We are a Zimbabwe-based healthcare appointment platform built to solve a real problem — patients struggling to find and book verified specialists quickly. We bridge the gap between patient and doctor, handling everything from scheduling to invoicing so that the only thing you focus on is your health.
							</p>
							<p className="about-text">
								Every specialist on our platform is verified and vetted. We work directly with doctors to manage their availability, coordinate appointments, and ensure a smooth professional experience for every patient.
							</p>
							<div className="about-features">
								{['Verified specialists only', 'Appointment coordination handled for you', 'Invoice sent via WhatsApp and email', 'No payment required on the platform'].map((feature, index) => (
									<div className="about-feature-item" key={index}>
										<FaCheckCircle className="about-feature-icon" />
										<span>{feature}</span>
									</div>
								))}
							</div>
						</div>
						<div className="col-lg-6">
							<div className="about-image-grid">
								<div className="about-image-main">
									<img src="https://images.unsplash.com/photo-1631217868264-e5b90bb7e133?w=600" alt="Healthcare" className="img-fluid" />
								</div>
								<div className="about-image-side">
									<img src="https://images.unsplash.com/photo-1582750433449-648ed127bb54?w=400" alt="Medical team" className="img-fluid" />
									<img src="https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?w=400" alt="Healthcare" className="img-fluid" />
								</div>
							</div>
						</div>
					</div>
				</div>
			</section>

			{/* Stats Section */}
			<section className="about-stats">
				<div className="container">
					<div className="row g-4">
						{stats.map((stat, index) => (
							<div className="col-sm-6 col-lg-3" key={index}>
								<div className="about-stat-card">
									<div className="about-stat-icon">{stat.icon}</div>
									<h3>{stat.number}</h3>
									<p>{stat.label}</p>
								</div>
							</div>
						))}
					</div>
				</div>
			</section>

			{/* Values Section */}
			<section className="about-values">
				<div className="container">
					<div className="text-center mb-5">
						<span className="about-label">OUR VALUES</span>
						<h2 className="about-heading">What drives us</h2>
						<p className="about-lead">The principles that guide everything we do</p>
					</div>
					<div className="row g-4">
						{values.map((value, index) => (
							<div className="col-lg-4" key={index}>
								<div className="about-value-card">
									<div className="about-value-icon">{value.icon}</div>
									<h4>{value.title}</h4>
									<p>{value.description}</p>
								</div>
							</div>
						))}
					</div>
				</div>
			</section>

			{/* Team Section */}
			<section className="about-team">
				<div className="container">
					<div className="text-center mb-5">
						<span className="about-label">OUR TEAM</span>
						<h2 className="about-heading">Meet our specialists</h2>
						<p className="about-lead">Experienced professionals dedicated to your health across Zimbabwe.</p>
					</div>
					<div className="row justify-content-center">
						{doctorContent}
					</div>
					<div className="text-center mt-4">
						<Link to="/doctors" className="about-cta-btn">View all doctors</Link>
					</div>
				</div>
			</section>

			<Footer />
		</>
	);
};

export default About;
